import {
  ApprovalableTypeEnum,
  ApprovaleableWorkflowEnum,
} from 'api-hooks/common';
import {
  PurchaseStatusEnum,
  SaleStatusEnum,
  TransactionalStatusEnum,
} from 'common/constants/enum';
import HOCWatchValue from 'components/common/hoc-watch-value';
import Separator from 'components/common/separator';
import Text from 'components/elements/text';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import BalanceAdjustmentDetail from './balance-adjustment-detail';
import IncomCostDetail from './income-cost-detail';
import ProjectDetail from './project/project-detail';
import PurchaseItems from './purchase';
import PurchaseDetail from './purchase-detail';
import RefundCardDetail from './refund/refund-card';
import ReleasePurchaseCard from './refund/release-purchase-card';
import ReleaseSaleCard from './refund/release-sale-card';
import SaleItems from './sale';
import SaleProject from './sale/project';
import SaleDetail from './sale-detail';
import ApprovalTipDetail from './tipping/tipping-detail';
import TourDetail from './tour';
import ApprovalBankCashCard from './transaction/bank-cash-card';
import OrderAdjustmentSection from './transaction/order-adjustment';
import ReceiptCard from './transaction/receipt-card';
import SettlementCard from './transaction/settlement-card';
import TransferCashCard from './transaction/transfer-cash-card';

export default function DetailSection() {
  const { t } = useTranslation();
  const values = HOCWatchValue({ keys: ['approvalable', 'approvalableType'] });
  const isIncludeVoid = (values.approvalable.status || '').includes('void');

  if (values.approvalableType === ApprovalableTypeEnum.Sale) {
    if (
      values.approvalable.workflow &&
      values.approvalable.workflow === ApprovaleableWorkflowEnum.SaleProject
    ) {
      return <SaleProject />;
    }

    const text = isIncludeVoid
      ? t('common:void_text_approval', { text: t('common:sale') })
      : values.approvalable.status === SaleStatusEnum.Waiting
      ? t('common:create_text_approval', { text: t('common:sale') })
      : values.approvalable.status === SaleStatusEnum.PendingRescheduleApproval
      ? t('common:reschedule_approval')
      : t('common:edit_text_approval', { text: t('common:sale') });

    return (
      <>
        <Text variant="HeadingXSmall">{text}</Text>
        <Separator gap={16} />
        <SaleDetail />
        <Separator gap={16} />
        <SaleItems />
      </>
    );
  }

  if (values.approvalableType === ApprovalableTypeEnum.Purchase) {
    const text = isIncludeVoid
      ? t('common:void_text_approval', { text: t('common:purchase') })
      : values.approvalable.status === PurchaseStatusEnum.PendingApproval
      ? t('common:edit_text_approval', { text: t('common:purchase') })
      : t('common:create_text_approval', { text: t('common:purchase') });

    return (
      <>
        <Text variant="HeadingXSmall">{text}</Text>
        <Separator gap={16} />
        <PurchaseDetail />
        <Separator gap={16} />
        <PurchaseItems />
        <Separator gap={16} />
      </>
    );
  }

  if (values.approvalableType === ApprovalableTypeEnum.IncomeAndCost) {
    return <IncomCostDetail />;
  }

  if (values.approvalableType === ApprovalableTypeEnum.BalanceAdjustment) {
    return (
      <>
        <Text variant="HeadingXSmall">
          {values.approvalable.status ===
          TransactionalStatusEnum.PendingVoidApproval
            ? t('common:void_text_approval', {
                text: t('common:balance_adjustment'),
              })
            : t('common:edit_text_approval', {
                text: t('common:balance_adjustment'),
              })}
        </Text>
        <Separator gap={16} />
        <BalanceAdjustmentDetail />
        <Separator gap={16} />
      </>
    );
  }

  if (
    values.approvalableType === ApprovalableTypeEnum.BankTransaction ||
    values.approvalableType === ApprovalableTypeEnum.CashTransaction
  ) {
    return (
      <>
        <Text variant="HeadingXSmall">
          {values.approvalable.status ===
          TransactionalStatusEnum.PendingVoidApproval
            ? t('common:void_text_approval', {
                text:
                  values.approvalableType ===
                  ApprovalableTypeEnum.BankTransaction
                    ? t('common:bank_transaction')
                    : t('common:cash_transactio '),
              })
            : t('common:edit_text_approval', {
                text:
                  values.approvalableType ===
                  ApprovalableTypeEnum.BankTransaction
                    ? t('common:bank_transaction')
                    : t('common:cash_transaction'),
              })}
        </Text>
        <Separator gap={16} />
        <ApprovalBankCashCard />
        <Separator gap={16} />
      </>
    );
  }

  if (values.approvalableType === ApprovalableTypeEnum.Transfer) {
    return (
      <>
        <Text variant="HeadingXSmall">
          {values.approvalable.status ===
          TransactionalStatusEnum.PendingVoidApproval
            ? t('common:void_text_approval', {
                text: t('common:transfer_cash'),
              })
            : t('common:edit_text_approval', {
                text: t('common:transfer_cash'),
              })}
        </Text>
        <Separator gap={16} />
        <TransferCashCard />
        <Separator gap={16} />
      </>
    );
  }

  if (values.approvalableType === ApprovalableTypeEnum.Project) {
    return (
      <>
        <Text variant="HeadingXSmall">
          {t('common:cancel_text_approval', {
            text: t('common:project'),
          })}
        </Text>
        <Separator gap={16} />
        <ProjectDetail />
        <Separator gap={16} />
      </>
    );
  }

  if (
    values.approvalableType === ApprovalableTypeEnum.OtherExpenses ||
    values.approvalableType === ApprovalableTypeEnum.OtherIncome
  ) {
    return (
      <>
        <Text variant="HeadingXSmall">
          {values.approvalable.status ===
          TransactionalStatusEnum.PendingVoidApproval
            ? t('common:void_text_approval', {
                text:
                  values.approvalableType === ApprovalableTypeEnum.OtherIncome
                    ? t('common:other_incomes')
                    : t('common:other_expenses'),
              })
            : t('common:edit_text_approval', {
                text:
                  values.approvalableType === ApprovalableTypeEnum.OtherIncome
                    ? t('common:other_incomes')
                    : t('common:other_expenses'),
              })}
        </Text>
        <Separator gap={16} />
        {values.approvalableType === ApprovalableTypeEnum.OtherIncome ? (
          <ApprovalTipDetail tipType="income" />
        ) : (
          <ApprovalTipDetail />
        )}
        <Separator gap={16} />
      </>
    );
  }

  if (values.approvalableType === ApprovalableTypeEnum.Refund) {
    const text = isIncludeVoid
      ? t('common:void_text_approval', { text: t('common:refund') })
      : values.approvalable.status === PurchaseStatusEnum.Pending
      ? t('common:edit_text_approval', { text: t('common:refund') })
      : t('common:create_text_approval', { text: t('common:refund') });

    return (
      <>
        <Text variant="HeadingXSmall">{text}</Text>
        <Separator gap={16} />
        <RefundCardDetail />
        <Separator gap={16} />
      </>
    );
  }

  if (values.approvalableType === ApprovalableTypeEnum.ReleaseSale) {
    return (
      <>
        <Text variant="HeadingXSmall">
          {t('common:release_refund_customer')}
        </Text>
        <Separator gap={16} />
        <ReleaseSaleCard />
        <Separator gap={16} />
      </>
    );
  }

  if (values.approvalableType === ApprovalableTypeEnum.ReleasePurchase) {
    return (
      <>
        <Text variant="HeadingXSmall">{t('common:release_refund_agent')}</Text>
        <Separator gap={16} />
        <ReleasePurchaseCard />
        <Separator gap={16} />
      </>
    );
  }

  if (
    values.approvalableType === ApprovalableTypeEnum.AccountPayable ||
    values.approvalableType === ApprovalableTypeEnum.AccountReceivable
  ) {
    return (
      <>
        <Text variant="HeadingXSmall">
          {values.approvalable.status ===
          TransactionalStatusEnum.PendingVoidApproval
            ? t('common:void_text_approval', { text: t('common:settlement') })
            : t('common:edit_text_approval', { text: t('common:settlement') })}
        </Text>
        <Separator gap={16} />
        <SettlementCard />
        <Separator gap={16} />
      </>
    );
  }

  if (values.approvalableType === ApprovalableTypeEnum.Tour) {
    const text = (values.approvalable.status || '').includes('cancel')
      ? t('common:cancel_text_approval', { text: t('common:tour') })
      : values.approvalable.status === SaleStatusEnum.PendingApproval
      ? t('common:edit_text_approval', { text: t('common:tour') })
      : t('common:close_text_approval', { text: t('common:tour') });
    return (
      <>
        <Text variant="HeadingXSmall">{text}</Text>
        <Separator gap={16} />
        <TourDetail />
        <Separator gap={16} />
      </>
    );
  }
  if (values.approvalableType === ApprovalableTypeEnum.OrderAdjustment) {
    return (
      <>
        <Text variant="HeadingXSmall">
          {values.approvalable.status ===
          TransactionalStatusEnum.PendingVoidApproval
            ? t('common:void_text_approval', {
                text: t('common:order_adjustment'),
              })
            : t('common:edit_text_approval', {
                text: t('common:order_adjustment'),
              })}
        </Text>
        <Separator gap={16} />
        <OrderAdjustmentSection />
        <Separator gap={16} />
      </>
    );
  }

  if (values.approvalableType === ApprovalableTypeEnum.Receipt) {
    return (
      <>
        <Text variant="HeadingXSmall">
          {values.approvalable.status ===
          TransactionalStatusEnum.PendingVoidApproval
            ? t('common:void_text_approval', {
                text: t('common:receipt'),
              })
            : t('common:edit_text_approval', {
                text: t('common:receipt'),
              })}
        </Text>
        <Separator gap={16} />
        <ReceiptCard />
        <Separator gap={16} />
      </>
    );
  }

  return <></>;
}
